import { useEffect, useRef } from "react";

import { addJWTInConfig } from "../../components/apiHooks/helpers";
import useApiRequestPost from "../../components/apiHooks/useApiRequestPost";
import { useConfigContext } from "../../contexts/ConfigContext/ConfigContext";
import { usePaymentContext } from "../../contexts/PaymentContext/PaymentContext";
import { PATH } from "../../router/path";

const HOST: string = process.env.CLOUD_RUN_GATEWAY_HOST ?? "";
const END_POINT: string = process.env.API_PAYMENT ?? "";

const url: string = `${HOST}${END_POINT}`;

interface GooglePayPaymentData {
	apiVersion: number;
	apiVersionMinor: number;
	paymentMethodData: {
		type: string;
		description: string;
		info: {
			cardNetwork: string;
			cardDetails: string;
		};
		tokenizationData: {
			type: string;
			token: string;
		};
	};
}

interface AdyenGooglePayPaymentRequest {
	paymentMethod: {
		type: "googlepay";
		googlePayToken: string;
	};
	amount: {
		currency: string;
		value: number;
	};
	countryCode: string;
	merchantAccount: string;
	returnUrl: string;
	hasAction: boolean;
	reference: string;
}

interface AdyenPaymentResponse {
	resultCode: string;
	action?: any;
	order?: any;
	donationToken?: string;
	pspReference?: string;
	merchantReference?: string;
	refusalReason?: string;
	refusalReasonCode?: string;
}

const usePostAdyenGooglePayment = () => {
	const { state: paymentReference } = usePaymentContext((state) => state.paymentReference);
	const { state: paymentData } = usePaymentContext((state) => state.paymentData);
	const {
		state: { user, config },
	} = useConfigContext((state) => state);
	const payload = {
		url,
		triggerOnMount: false,
		axiosConfig: addJWTInConfig(undefined, user?.session_id?.toString() ?? ""),
		swrConfig: {
			revalidateOnFocus: false,
			revalidateOnReconnect: false,
			revalidateIfStale: false,
			shouldRetryOnError: false,
			errorRetryInterval: 0,
			errorRetryCount: 0,
		},
	};

	const actionRef = useRef<any>(null);
	const {
		data: response,
		error,
		postData,
	} = useApiRequestPost<AdyenPaymentResponse, AdyenGooglePayPaymentRequest>(payload);

	const submitGooglePayment = (googlePayData: any, component: any, actions: any) => {
		// Extract the Google Pay payment token
		// Handle both onSubmit format and onAuthorized format
		const paymentMethodData =
			googlePayData.paymentMethodData || googlePayData.authorizedEvent?.paymentMethodData;

		if (!paymentMethodData?.tokenizationData?.token) {
			console.error("Invalid Google Pay data structure:", googlePayData);
			actions?.reject?.({
				transactionState: "ERROR",
				error: {
					reason: "PAYMENT_DATA_INVALID",
					message: "Invalid payment data received",
				},
			});

			return;
		}

		const googlePayToken = paymentMethodData.tokenizationData.token;

		const requestPayload: any = {
			paymentMethod: {
				type: "googlepay",
				googlePayToken: googlePayToken,
			},
			amount: {
				currency: "SGD",
				value: paymentData?.orders_figures?.net_value ?? 0,
			},
			countryCode: "SG",
			merchantAccount: config.adyen_merchant_name?.name,
			returnUrl: `${globalThis.location.origin}${PATH.ORDER_CONFIRMATION}`,
			reference: paymentReference ?? "",
			paymentReference,
			hasAction: false,
		};

		// Store actions before making API call
		actionRef.current = actions;

		// Add error handling for the API call
		postData(requestPayload).catch((error) => {
			console.error("Google Pay API request failed:", error);
			actions?.reject?.({
				transactionState: "ERROR",
				error: {
					reason: "PAYMENT_AUTHORIZATION",
					message: "Payment processing failed",
					intent: "PAYMENT_AUTHORIZATION",
				},
			});
		});
	};

	useEffect(() => {
		if (!response) return;

		const { resultCode } = response;

		if (actionRef.current) {
			// Handle different result codes
			switch (resultCode) {
				case "Refused":
				case "Error":
					actionRef.current.reject({
						transactionState: "ERROR",
						error: {
							reason: "PAYMENT_AUTHORIZATION",
							message: response.refusalReason || "Payment was refused",
							intent: "PAYMENT_AUTHORIZATION",
						},
					});
					break;

				case "RedirectShopper":
				case "IdentifyShopper":
				case "ChallengeShopper":
					actionRef.current.resolve({
						transactionState: "PENDING",
					});
					break;

				case "Authorised":
				default:
					actionRef.current.resolve({
						transactionState: "SUCCESS",
					});
					break;
			}
		}
	}, [response, actionRef]);

	// Handle API errors
	useEffect(() => {
		if (!error || !actionRef.current) return;

		console.error("Google Pay API error:", error);

		actionRef.current?.reject?.({
			transactionState: "ERROR",
			error: {
				reason: "PAYMENT_AUTHORIZATION",
				message: "Payment processing failed",
				intent: "PAYMENT_AUTHORIZATION",
			},
		});

		// Clear the action ref after handling the error
		actionRef.current = null;
	}, [error]);

	return {
		submitGooglePayment,
		isLoading: !!response,
		response,
		error,
	};
};

export default usePostAdyenGooglePayment;
